const apiDomain = "https://ninefinds.com";
const supabaseStorageDomain = import.meta.env.VITE_SUPABASE_STORAGE_DOMAIN || "https://rcppkrbujyeycqrmmmxi.supabase.co";

// Derived from Vite's built-in mode instead of hardcoded — import.meta.env.PROD
// is true for `npm run build` (production) and false for `npm run dev` (local).
// This was previously hardcoded to "production" unconditionally, which forced
// third-party SDKs (e.g. Cashfree) into production mode even during local dev,
// even though the local backend issues sandbox-scoped session tokens for it —
// a mismatch that made those sessions fail with "token is not present".
const environment = import.meta.env.PROD ? "production" : "development";

const AppConfig = {
  apiDomain,
  supabaseStorageDomain,
  feedbackEndpoint: "/api/v1/feedbacks/getfeedback",
  announcementEndpoint: "/api/v1/announcements/getannouncements",
  environment, // "production" (npm run build) or "development" (npm run dev)
  defaultLocale: "en-IN",
  featureFlags: {
    enableTestimonials: true,
    enableBillingTab: false,
    enableBusinessSections: true,
    enablePrintBill: true,
    enableNineFindsOrders: true,
    enableDineInBoard: true,
    enableNineFindsDigitalAssets: true,
    // NFC Card tab in the digital-assets modal — needs POST
    // /api/v1/imagegen/nfc-card on the backend. Turn off if the frontend ships
    // before that endpoint is deployed.
    enableNfcCardAsset: true,
    // My Earnings → Activity tab (draft businesses / curator setups). Off: the
    // tab shows as "Coming soon"; draft creation and curator work live in the
    // Nine Setup program meanwhile. While off, PendingBusinessCard (payment-link
    // regenerate / invoice link on existing submissions) is unreachable here.
    enableOnboardingActivityTab: true,
    // Slot availability calendar widget (ProductDetailsV2, Rental listings).
    // Global kill switch. Per business it's also gated by the business
    // config flag carried on the product (useProductInfo) - see
    // docs/plans/slot-availability-calendar-plan.md. Writes only ever happen
    // on an explicit owner action (Turn on / Manage), never on page load.
    enableSlotCalendar: true,
    // AI batch image edits — curator Tasks tab, "AI batch" on Claimed cards,
    // Admin → AI Batch Edits. Needs /api/v1/imagegen/batches live with the
    // backend's ai.image.batch.enabled flag on; turn this on together with it.
    enableAiBatchEdits: true,
    // Catalogue PDF import — "Catalogue imports" in the curator Tasks tab and the
    // /nine-setup/catalog-import/:importId review route. docs/plans/
    // catalog-import-ui-plan.md. Also gated at runtime by GET /api/v1/catalog-
    // imports/config (`enabled`), so the switch stays hidden until the backend's
    // catalog.import.enabled flag is on.
    enableCatalogImport: true,
    // Voice edit ("Edit by voice" on owner product cards) — docs/plans/
    // voice-edit-ui-plan.md. Also gated at runtime by GET /api/v1/voice/
    // capabilities. Turn on together with the backend's voice.enabled flag.
    enableVoiceEdit: true,
    // Enable after the product migration and backend capability are deployed.
    enableVoiceCreate: true,
    // Bulk image analysis v2 ("Smart photos" tab in the Bulk upload modal) — docs/plans/
    // bulk-image-analysis-v2-ui-plan.md. Also gated at runtime by GET /api/v2/bulk-image-
    // analysis/config (`enabled`). Turn on together with the backend's bia.enabled flag.
    enableBulkImageAnalysisV2: true,
  },
  // iOS-wrapper "web gates": profile tabs that collect payment and therefore
  // must not run inside the native iOS WebView (App Store IAP policy). When a
  // gate is ON, that tab is hidden / its deep link blocked in the iOS wrapper
  // and the entry point opens the equivalent page on the website in the external
  // browser instead. Each gate is independent so it can be toggled on/off here
  // with no code change. Only takes effect inside the iOS wrapper
  // (isIosWebView()); Android and the web are never affected.
  iosWebGates: {
    onboarding: true,     // "My Earnings" / onboarding → opens on website
    nineSetup: true,      // "Nine Setup" (UPI payment QR) → opens on website
    subscriptions: false, // Subscriptions tab → unlocked, stays in-app
  },
  polling: {
    configIntervalMs: 12 * 60 * 60 * 1000, // How often to re-fetch UI config (5 minutes)
    reloadCooldownMs: 60 * 1000,      // Minimum gap between hard reloads (30 seconds)
  },
};

export default AppConfig;
